import { useEffect, useState } from 'react';
import {
  FormFieldBlueprint,
  createFormField,
} from '@backstage/plugin-scaffolder-react/alpha';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Typography,
  CircularProgress,
} from '@material-ui/core';

interface Environment {
  name: string;
  label: string;
  awsAccount: string;
  region: string;
}

interface InfrastructureContextResponse {
  environments: Environment[];
  clusters: { name: string; environment: string; region: string; account: string; namespace: string }[];
  generatedAt: string;
}

/**
 * DynamicContextPicker — fetches available environments from backend at render time.
 * POC for CIN-14065: proves dynamic data loading in scaffolder templates.
 */
function DynamicContextPickerComponent(
  props: FieldExtensionComponentProps<string[]>,
) {
  const { onChange, rawErrors, formData = [] } = props;
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    async function fetchContext() {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('infrastructure-context');
        const [res] = await Promise.all([
          fetchApi.fetch(`${baseUrl}/environments`),
          new Promise(resolve => setTimeout(resolve, 4000)),
        ]);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        const data: InfrastructureContextResponse = await res.json();
        if (!cancelled) {
          setEnvironments(data.environments);
          setGeneratedAt(data.generatedAt);
          setLoading(false);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e.message ?? 'Failed to load environments');
          setLoading(false);
        }
      }
    }
    fetchContext();
    return () => { cancelled = true; };
  }, [discoveryApi, fetchApi]);

  const handleToggle = (envName: string) => {
    const current = formData || [];
    const next = current.includes(envName)
      ? current.filter(e => e !== envName)
      : [...current, envName];
    onChange(next);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="textSecondary">
          Fetching available environments from infrastructure context API...
        </Typography>
      </div>
    );
  }

  if (error) {
    return (
      <Typography color="error" variant="body2">
        Error loading environments: {error}
      </Typography>
    );
  }

  return (
    <FormGroup>
      <Typography variant="caption" color="textSecondary" gutterBottom>
        Dynamically loaded at {generatedAt} — {environments.length} environments available
      </Typography>
      {environments.map(env => (
        <FormControlLabel
          key={env.name}
          control={
            <Checkbox
              checked={(formData || []).includes(env.name)}
              onChange={() => handleToggle(env.name)}
              color="primary"
            />
          }
          label={`${env.label} (${env.region} / ${env.awsAccount})`}
        />
      ))}
      {rawErrors && rawErrors.length > 0 && (
        <FormHelperText error>{rawErrors.join(', ')}</FormHelperText>
      )}
    </FormGroup>
  );
}

/** The form field binding for the scaffolder */
const dynamicContextFormField = createFormField({
  name: 'DynamicContextPicker',
  component: DynamicContextPickerComponent,
});

/** The extension blueprint registered in App.tsx */
export const dynamicContextPickerExtension = FormFieldBlueprint.make({
  name: 'dynamic-context-picker',
  params: {
    field: async () => dynamicContextFormField,
  },
});
